import { MutableRefObject, RefCallback, useCallback, useMemo } from 'react';

/**
 * Merges multiple refs into a single ref.
 *
 * @param refs - The refs to merge.
 * @returns The merged ref.
 */
export default function useMergeRef<Type>(
  ...refs: (MutableRefObject<Type | null> | RefCallback<Type | null> | undefined | null)[]
) {
  // #region Handlers
  const applyRef = useCallback<RefCallback<Type>>(
    (instance: Type | null) => {
      refs.forEach((ref) => {
        if (!ref) return;

        if (typeof ref === 'function') ref(instance);
        // NOTE: ref is reassigned through `current`.
        // eslint-disable-next-line no-param-reassign
        else ref.current = instance;
      });
    },
    [refs],
  );
  // #endregion

  // #region Computed Values
  const mergedRef = useMemo(() => (refs.every((ref) => !ref) ? null : applyRef), [applyRef, refs]);
  // #endregion

  return mergedRef;
}
